// Landing page for the Jammable MCP server: what it is, the tools it
// exposes, and copy-paste setup for Claude Code / Claude Desktop / Cursor.
// Visual language mirrors the head-term hubs and TrainVoiceBanner: pale
// gradient panels, lime highlighter, tilted white cards, dashed step thread.
import React, { useState } from "react";
import { NextSeo } from "next-seo";
import Link from "next/link";
import { IoSparkles, IoArrowForward } from "react-icons/io5";
import { FaSearch, FaMusic, FaMicrophone, FaKey } from "react-icons/fa";
import { HiOutlineStatusOnline } from "react-icons/hi";
import { BsCoin, BsRobot } from "react-icons/bs";

const ENDPOINT = "https://www.jammable.com/api/mcp/mcp";

const TOOLS = [
  { name: "search_voices", icon: <FaSearch size={15} />, chip: "tw-bg-gradient-to-br tw-from-violet-500 tw-to-purple-700", desc: "Search 100,000+ community AI voices by name or category." },
  { name: "create_cover", icon: <FaMusic size={15} />, chip: "tw-bg-gradient-to-br tw-from-fuchsia-500 tw-to-pink-600", desc: "Turn a song into an AI cover in any voice. $0.25 per minute of audio." },
  { name: "text_to_speech", icon: <FaMicrophone size={15} />, chip: "tw-bg-gradient-to-br tw-from-lime-500 tw-to-emerald-600", desc: "Speak up to 2,500 characters in any Jammable voice. Billed per minute." },
  { name: "get_cover", icon: <HiOutlineStatusOnline size={16} />, chip: "tw-bg-gradient-to-br tw-from-sky-500 tw-to-blue-600", desc: "Poll a cover's status and grab the finished audio URL." },
  { name: "get_voice", icon: <BsRobot size={15} />, chip: "tw-bg-gradient-to-br tw-from-amber-500 tw-to-orange-600", desc: "Fetch details and stats for a single voice model." },
  { name: "get_credits", icon: <BsCoin size={15} />, chip: "tw-bg-gradient-to-br tw-from-gray-700 tw-to-gray-900", desc: "Check your remaining API credit balance." },
];

const SNIPPETS = [
  {
    label: "Claude Code",
    hint: "one command",
    code: `claude mcp add --transport http jammable ${ENDPOINT} \\
  --header "Authorization: Bearer jmb_live_YOUR_KEY"`,
  },
  {
    label: "Claude Desktop · Cursor · Windsurf",
    hint: "mcp.json",
    code: `{
  "mcpServers": {
    "jammable": {
      "url": "${ENDPOINT}",
      "headers": { "Authorization": "Bearer jmb_live_YOUR_KEY" }
    }
  }
}`,
  },
];

const FAQ = [
  {
    q: "What is the Jammable MCP server?",
    a: "It is a Model Context Protocol endpoint that lets AI agents like Claude, Cursor and ChatGPT use Jammable directly: searching voices, creating AI covers, generating text-to-speech and checking results, all through natural conversation.",
  },
  {
    q: "How do I get an API key?",
    a: "Sign in to Jammable, open the Developer Dashboard, and create a key. Keys look like jmb_live_... and are passed to the MCP server as a bearer token.",
  },
  {
    q: "What does it cost?",
    a: "Generation calls (create_cover, text_to_speech) are billed at $0.25 per minute of audio processed, deducted from your API balance after completion. Failed jobs are not charged, and searching or checking status is free. The Power plan includes $25 of monthly API credits, and you can top up from the Developer Dashboard.",
  },
  {
    q: "What audio can I use for covers?",
    a: "create_cover takes a publicly accessible audio file URL (.mp3 or .wav). YouTube and streaming links are not supported. You are responsible for having rights to audio you submit.",
  },
  {
    q: "Which clients are supported?",
    a: "Any MCP client that supports Streamable HTTP remote servers: Claude Code, Claude Desktop, Cursor, Windsurf, and most agent frameworks. No local install is needed.",
  },
];

const STEPS = [
  { n: "1", chip: "tw-bg-gradient-to-br tw-from-violet-500 tw-to-purple-700", title: "Create an API key", body: <>Grab a <span className="tw-font-mono tw-text-xs tw-font-bold">jmb_live_</span> key from the <Link href="/developers/dashboard" className="tw-font-black tw-text-purple-700">Developer Dashboard</Link>.</> },
  { n: "2", chip: "tw-bg-gradient-to-br tw-from-fuchsia-500 tw-to-pink-600", title: "Add the server", body: <>Paste one of the snippets into your client. No install, it is a remote server.</> },
  { n: "3", chip: "tw-bg-gradient-to-br tw-from-lime-500 tw-to-emerald-600", title: "Ask for a cover", body: <>&ldquo;Find a SpongeBob voice and cover this track&rdquo; and your agent does the rest.</> },
];

const McpPage = () => {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = (text: string, key: string) => {
    navigator.clipboard?.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className="tw-mx-auto tw-w-full tw-max-w-5xl tw-px-4 tw-pb-24 tw-pt-8">
      <NextSeo
        title="Jammable MCP Server: AI Covers from Claude, Cursor & Agents"
        description="Connect AI agents to Jammable with the Model Context Protocol. Search 100,000+ voices, create AI covers and text-to-speech from Claude Code, Claude Desktop, Cursor and more."
        canonical="https://www.jammable.com/mcp"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        }}
      />

      {/* Hero */}
      <section className="tw-relative tw-overflow-hidden tw-rounded-[2rem] tw-border tw-border-solid tw-border-white tw-bg-gradient-to-br tw-from-violet-100 tw-via-white tw-to-fuchsia-100 tw-px-6 tw-py-10 tw-shadow-[0_24px_70px_-34px_rgba(120,45,200,0.5)] md:tw-px-12 md:tw-py-14">
        <div className="tw-pointer-events-none tw-absolute tw--bottom-24 tw--left-14 tw-h-56 tw-w-56 tw-rounded-full tw-bg-lime-300/50 tw-blur-3xl" />
        <div className="tw-pointer-events-none tw-absolute tw--top-20 tw--right-10 tw-h-48 tw-w-48 tw-rounded-full tw-bg-fuchsia-300/40 tw-blur-3xl" />
        <div className="tw-relative tw-flex tw-flex-col tw-items-center tw-gap-8 lg:tw-flex-row lg:tw-items-center lg:tw-justify-between">
          <div className="tw-max-w-xl tw-text-center lg:tw-text-left">
            <div className="tw-mx-auto tw-mb-4 tw-flex tw-w-fit tw-items-center tw-gap-2 tw-rounded-full tw-bg-black tw-px-4 tw-py-1.5 tw-text-xs tw-font-black tw-uppercase tw-tracking-wide tw-text-lime-300 lg:tw-mx-0">
              <IoSparkles size={13} />
              Model Context Protocol
            </div>
            <h1 className="tw-m-0 tw-text-4xl tw-font-black tw-leading-[1.05] tw-tracking-tight md:tw-text-[3.4rem]">
              Use Jammable from your{" "}
              <span className="tw-relative tw-inline-block">
                <span className="tw-absolute tw-inset-x-[-2px] tw-bottom-[0.08em] tw-top-[0.52em] tw--rotate-1 tw-rounded-md tw-bg-lime-300" />
                <span className="tw-relative">AI agent</span>
              </span>
            </h1>
            <p className="tw-mx-auto tw-mt-4 tw-max-w-lg tw-text-base tw-font-semibold tw-text-black/55 lg:tw-mx-0">
              Claude, Cursor and any MCP client can search 100,000+ AI voices,
              create covers and generate text-to-speech. One endpoint, no
              install.
            </p>
            <div className="tw-mt-6 tw-flex tw-flex-wrap tw-justify-center tw-gap-3 lg:tw-justify-start">
              <Link
                href="/developers/dashboard"
                className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-2xl tw-bg-purple-600 tw-px-6 tw-py-3.5 tw-text-base tw-font-bold tw-text-white tw-no-underline tw-shadow-[0_12px_28px_-10px_rgba(124,58,237,0.7)] tw-transition hover:tw--translate-y-0.5 hover:tw-bg-purple-700"
              >
                Get your API key
                <IoArrowForward size={16} />
              </Link>
              <a
                href="/docs"
                className="tw-inline-flex tw-items-center tw-rounded-2xl tw-border tw-border-solid tw-border-gray-200 tw-bg-white tw-px-6 tw-py-3.5 tw-text-base tw-font-bold tw-text-gray-800 tw-no-underline tw-shadow-sm tw-transition hover:tw--translate-y-0.5 hover:tw-border-gray-900"
              >
                REST API docs
              </a>
            </div>
          </div>
          {/* Endpoint card, tilted like the train-banner steps card */}
          <div className="tw-w-full tw-max-w-md tw-rounded-3xl tw-bg-white tw-p-5 tw-shadow-[0_24px_60px_-28px_rgba(80,30,160,0.5)] tw-ring-1 tw-ring-black/5 tw-transition tw-duration-200 hover:tw--translate-y-1 lg:tw-w-[420px] lg:tw--rotate-1 lg:hover:tw-rotate-0">
            <div className="tw-flex tw-items-center tw-justify-between">
              <div className="tw-text-xs tw-font-black tw-uppercase tw-tracking-wide tw-text-black/40">
                Endpoint
              </div>
              <span className="tw-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-lime-100 tw-px-2.5 tw-py-1 tw-text-[11px] tw-font-black tw-text-lime-700">
                <span className="tw-h-1.5 tw-w-1.5 tw-animate-pulse tw-rounded-full tw-bg-lime-500" />
                LIVE
              </span>
            </div>
            <button
              type="button"
              onClick={() => copy(ENDPOINT, "endpoint")}
              className="tw-mt-3 tw-w-full tw-cursor-pointer tw-rounded-2xl tw-border-0 tw-bg-gray-900 tw-p-4 tw-text-left tw-font-mono tw-text-[13px] tw-leading-snug tw-text-lime-300 tw-transition hover:tw-bg-gray-800"
              title="Click to copy"
            >
              {ENDPOINT}
              <div className="tw-mt-2 tw-font-sans tw-text-[11px] tw-font-bold tw-text-gray-400">
                {copied === "endpoint" ? "Copied ✓" : "Streamable HTTP · click to copy"}
              </div>
            </button>
            <div className="tw-mt-3 tw-flex tw-items-center tw-gap-2 tw-rounded-2xl tw-bg-gray-50 tw-px-3.5 tw-py-2.5 tw-text-xs tw-font-semibold tw-text-black/50">
              <FaKey size={11} className="tw-shrink-0 tw-text-purple-500" />
              Auth: Bearer&nbsp;
              <span className="tw-font-mono tw-font-bold tw-text-black/70">jmb_live_...</span>
            </div>
          </div>
        </div>
      </section>

      {/* Tools */}
      <div className="tw-mt-16 tw-flex tw-items-center tw-gap-3">
        <div className="tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-gradient-to-br tw-from-violet-500 tw-to-purple-700 tw-text-white tw-shadow-[0_6px_14px_-6px_rgba(0,0,0,0.4)]">
          <BsRobot size={16} />
        </div>
        <h2 className="tw-m-0 tw-text-2xl tw-font-black tw-tracking-tight md:tw-text-3xl">
          What your agent can do
        </h2>
      </div>
      <div className="tw-mt-5 tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2 lg:tw-grid-cols-3">
        {TOOLS.map((t) => (
          <div
            key={t.name}
            className="tw-group tw-rounded-3xl tw-border tw-border-solid tw-border-gray-100 tw-bg-white tw-p-5 tw-shadow-sm tw-transition tw-duration-200 hover:tw--translate-y-1 hover:tw-shadow-[0_18px_40px_-20px_rgba(80,30,160,0.4)]"
          >
            <div className={`tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-2xl tw-text-white tw-shadow-[0_6px_14px_-6px_rgba(0,0,0,0.4)] ${t.chip}`}>
              {t.icon}
            </div>
            <div className="tw-mt-3 tw-font-mono tw-text-sm tw-font-bold tw-text-gray-900">
              {t.name}
            </div>
            <p className="tw-m-0 tw-mt-1 tw-text-sm tw-leading-relaxed tw-text-black/55">
              {t.desc}
            </p>
          </div>
        ))}
      </div>

      {/* Setup */}
      <section className="tw-mt-16 tw-overflow-hidden tw-rounded-[2rem] tw-border tw-border-solid tw-border-white tw-bg-gradient-to-br tw-from-lime-100 tw-via-white tw-to-fuchsia-100 tw-px-6 tw-py-9 tw-shadow-[0_24px_70px_-42px_rgba(120,45,200,0.5)] md:tw-px-10">
        <div className="tw-flex tw-flex-col tw-gap-8 lg:tw-flex-row lg:tw-items-start lg:tw-justify-between">
          <div className="tw-w-full lg:tw-max-w-sm">
            <h2 className="tw-m-0 tw-text-2xl tw-font-black tw-tracking-tight md:tw-text-3xl">
              Set up in{" "}
              <span className="tw-relative tw-inline-block">
                <span className="tw-absolute tw-inset-x-[-2px] tw-bottom-[0.08em] tw-top-[0.52em] tw--rotate-1 tw-rounded-md tw-bg-lime-300" />
                <span className="tw-relative">30 seconds</span>
              </span>
            </h2>
            <div className="tw-mt-6 tw-rounded-3xl tw-bg-white tw-p-5 tw-shadow-[0_24px_60px_-28px_rgba(80,30,160,0.5)] tw-ring-1 tw-ring-black/5 lg:tw--rotate-1">
              {STEPS.map((s, i) => (
                <div key={s.n} className="tw-flex tw-gap-3.5">
                  <div className="tw-flex tw-flex-col tw-items-center">
                    <div className={`tw-flex tw-h-10 tw-w-10 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-2xl tw-text-sm tw-font-black tw-text-white tw-shadow-[0_6px_14px_-6px_rgba(0,0,0,0.4)] ${s.chip}`}>
                      {s.n}
                    </div>
                    {i < STEPS.length - 1 && (
                      <span className="tw-my-1 tw-h-6 tw-w-0 tw-border-0 tw-border-l-2 tw-border-dashed tw-border-gray-200" />
                    )}
                  </div>
                  <div className={i < STEPS.length - 1 ? "tw-pb-2" : ""}>
                    <div className="tw-text-sm tw-font-black">{s.title}</div>
                    <p className="tw-m-0 tw-mt-0.5 tw-text-[13px] tw-leading-relaxed tw-text-black/55">
                      {s.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="tw-w-full tw-min-w-0 lg:tw-flex-1">
            {SNIPPETS.map((s) => (
              <div key={s.label} className="tw-mb-4">
                <div className="tw-mb-1.5 tw-flex tw-items-center tw-justify-between">
                  <div className="tw-text-sm tw-font-black">
                    {s.label}{" "}
                    <span className="tw-ml-1 tw-rounded-full tw-bg-black/5 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-bold tw-text-black/50">
                      {s.hint}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => copy(s.code, s.label)}
                    className="tw-cursor-pointer tw-rounded-full tw-border tw-border-solid tw-border-gray-200 tw-bg-white tw-px-3 tw-py-1 tw-text-xs tw-font-bold tw-text-gray-600 tw-transition hover:tw-border-gray-900"
                  >
                    {copied === s.label ? "Copied ✓" : "Copy"}
                  </button>
                </div>
                <pre className="tw-m-0 tw-overflow-x-auto tw-rounded-2xl tw-bg-gray-900 tw-p-4 tw-text-xs tw-leading-relaxed tw-text-gray-100 tw-shadow-[0_16px_40px_-24px_rgba(0,0,0,0.6)]">
                  <code>{s.code}</code>
                </pre>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <h2 className="tw-mb-5 tw-mt-16 tw-text-2xl tw-font-black tw-tracking-tight md:tw-text-3xl">
        Common questions
      </h2>
      <div className="tw-grid tw-grid-cols-1 tw-gap-3 md:tw-grid-cols-2">
        {FAQ.map((f) => (
          <div
            key={f.q}
            className="tw-rounded-3xl tw-border tw-border-solid tw-border-gray-100 tw-bg-white tw-p-5 tw-shadow-sm"
          >
            <div className="tw-text-[15px] tw-font-black">{f.q}</div>
            <p className="tw-m-0 tw-mt-1.5 tw-text-sm tw-leading-relaxed tw-text-black/55">
              {f.a}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default McpPage;
